/**
 * The proactive half: refresh the session BEFORE the access token expires, for as long
 * as the user is actually using the app.
 *
 * Everything funnels into one `evaluate()` and the decision itself lives in
 * `planRefresh` (pure). This module only supplies wake-ups:
 *
 *   - a timer aimed at the due instant (re-aimed whenever the token changes — including a
 *     rotation performed by another tab, which arrives as a `storage` event);
 *   - `visibilitychange` / `focus` / `online`, because a background tab has its timers
 *     clamped and a laptop lid closes clocks entirely — the tab must re-check on waking;
 *   - activity marks, so a user who goes idle past the due time and then returns is
 *     refreshed on their first keystroke rather than at the next tick;
 *   - a coarse interval as insurance against any of the above being throttled away.
 *
 * Idle users are deliberately NOT refreshed: `useInactivityTimeout` shares the same activity
 * store and owns what happens to them.
 */

import { getUserData, ISADMINLOGGED } from '@/constants/global';

import { subscribeToTokenChange } from '../authz/token-events';
import { IDLE_WINDOW_MS, msSinceActivity, startActivityTracking, subscribeToActivity } from './activity';
import { endSession, refreshSession } from './refresh-session';
import { isAccessExpired, planRefresh, readTokenTiming } from './token-lifetime';

const HEARTBEAT_MS = 30_000;
/** Retry ladder for a transient refresh failure while the access token is still valid. */
const TRANSIENT_BACKOFF_MS = [15_000, 30_000, 60_000];
/** After a wake-up burst (focus + visibility + activity all at once) evaluate once, not thrice. */
const EVALUATE_COALESCE_MS = 250;

let running = false;

export const startSessionKeeper = (): (() => void) => {
	if (running) return () => undefined;
	running = true;

	let dueTimer: ReturnType<typeof setTimeout> | null = null;
	let coalesceTimer: ReturnType<typeof setTimeout> | null = null;
	let transientAttempts = 0;
	let evaluating = false;

	const clearDueTimer = () => {
		if (dueTimer) clearTimeout(dueTimer);
		dueTimer = null;
	};

	const aimAt = (atMs: number) => {
		clearDueTimer();
		// setTimeout caps at 2^31-1 ms; the heartbeat covers anything beyond it anyway.
		const delay = Math.min(Math.max(0, atMs - Date.now()), 2_147_483_647);
		dueTimer = setTimeout(() => void evaluate(), delay);
	};

	const evaluate = async () => {
		if (!running || evaluating) return;
		evaluating = true;
		try {
			if (!ISADMINLOGGED()) return;
			const { token, refreshToken } = getUserData();
			const timing = readTokenTiming(token, refreshToken);
			if (!timing) return;

			const nowMs = Date.now();
			const plan = planRefresh({ nowMs, timing, msSinceActivity: msSinceActivity(nowMs), idleWindowMs: IDLE_WINDOW_MS });

			switch (plan.kind) {
				case 'wait':
					transientAttempts = 0;
					aimAt(plan.dueAtMs);
					return;
				case 'idle':
					// The idle flow owns this user. Nothing to schedule; activity will wake us.
					return;
				case 'dead':
					if (isAccessExpired(timing, nowMs)) endSession('expired');
					return;
				case 'refresh': {
					const outcome = await refreshSession(token);
					if (outcome === 'rotated' || outcome === 'adopted') {
						transientAttempts = 0;
						// The token-change notification re-aims the timer; nothing else to do.
						return;
					}
					if (outcome === 'dead') {
						endSession('expired');
						return;
					}
					// transient: try again while there is still time; past expiry the reactive net takes over.
					const backoff = TRANSIENT_BACKOFF_MS[Math.min(transientAttempts, TRANSIENT_BACKOFF_MS.length - 1)];
					transientAttempts += 1;
					if (!isAccessExpired(timing, Date.now())) aimAt(Date.now() + backoff);
					return;
				}
				default:
					return;
			}
		} finally {
			evaluating = false;
		}
	};

	const requestEvaluate = () => {
		if (coalesceTimer) return;
		coalesceTimer = setTimeout(() => {
			coalesceTimer = null;
			void evaluate();
		}, EVALUATE_COALESCE_MS);
	};

	const onVisibility = () => {
		if (document.visibilityState === 'visible') requestEvaluate();
	};

	const releaseActivity = startActivityTracking();
	const unsubscribeActivity = subscribeToActivity(requestEvaluate);
	const unsubscribeToken = subscribeToTokenChange(requestEvaluate);
	document.addEventListener('visibilitychange', onVisibility);
	window.addEventListener('focus', requestEvaluate);
	window.addEventListener('online', requestEvaluate);
	const heartbeat = setInterval(() => void evaluate(), HEARTBEAT_MS);

	void evaluate();

	return () => {
		running = false;
		clearDueTimer();
		if (coalesceTimer) clearTimeout(coalesceTimer);
		coalesceTimer = null;
		clearInterval(heartbeat);
		document.removeEventListener('visibilitychange', onVisibility);
		window.removeEventListener('focus', requestEvaluate);
		window.removeEventListener('online', requestEvaluate);
		unsubscribeToken();
		unsubscribeActivity();
		releaseActivity();
	};
};
